/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { Notification } from './notification'
import { StatNotification } from './stat-notification'
import { Notice } from './notice'
import { ReadNoticesReq } from './read-notices-req'
import { NotificationReq } from './notification-req'
import { StatusEnum } from './status-enum'
import { WithDrawnNotificationReq } from './with-drawn-notification-req'
import { UserForegroundDomainEnum } from './user-foreground-domain-enum'
import { UploadFile } from './upload-file'
import { NotificationUploadResponse } from './notification-upload-response'
import { NotificationReflection } from './notification-reflection'
import { QueryFrontendInformationsQuery } from './query-frontend-informations-query'
import { StatFrontendInformationsQuery } from './stat-frontend-informations-query'
import { QueryNotificationsQuery } from './query-notifications-query'
import { LoadNotificationQuery } from './load-notification-query'
import { QueryReceivedNotificationsQuery } from './query-received-notifications-query'
import { DownloadNotificationTemplateQuery } from './download-notification-template-query'
import { QueryNotificationReflectionsQuery } from './query-notification-reflections-query'

const buildConfig = createConfigBuilder('growp')

export const notificationApi = {
  queryFrontendInformationsConfig(
    queryFrontendInformationsQuery?: QueryFrontendInformationsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/notification/frontend-informations',
      {},
      queryFrontendInformationsQuery
    )
  },

  /**
   * @summary 【前台】用户查询资讯列表
   * @param [queryFrontendInformationsQuery]
   * @param [queryFrontendInformationsQuery.isReceived] 是否是接收列表
   * @param [queryFrontendInformationsQuery.paginated] 是否分页，默认 1
   * @param [queryFrontendInformationsQuery.page] 显示的数据页，默认 1
   * @param [queryFrontendInformationsQuery.perPage] 每页显示数量，默认 20
   * @param [queryFrontendInformationsQuery.sort]
   * @param [queryFrontendInformationsQuery.titleLike]
   * @param [queryFrontendInformationsQuery.publishedAtEgt] 发送时间大于
   * @param [queryFrontendInformationsQuery.publishedAtElt] 发送时间小于
   * @param [queryFrontendInformationsQuery.method] 发送方式
   * @param [queryFrontendInformationsQuery.status] 发送状态
   * @param [queryFrontendInformationsQuery.createrNameLike] 发送人姓名
   * @param [queryFrontendInformationsQuery.activityId]
   * @param [queryFrontendInformationsQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<Notification[]>
   */
  queryFrontendInformations<TQuery extends QueryFrontendInformationsQuery>(
    queryFrontendInformationsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Notification, TQuery['expand']>[]> {
    return axios(
      notificationApi.queryFrontendInformationsConfig(
        queryFrontendInformationsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new Notification(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryFrontendInformations<TQuery extends QueryFrontendInformationsQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Notification, TQuery['expand']>[]> {
    return useQueryApi<Expand<Notification, TQuery['expand']>[]>(
      notificationApi.queryFrontendInformations,
      'Notification.queryFrontendInformations',
      queryOptions,
      query
    )
  },

  statFrontendInformationsConfig(
    statFrontendInformationsQuery?: StatFrontendInformationsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/notification/frontend-informations/stats',
      {},
      statFrontendInformationsQuery
    )
  },

  /**
   * @summary 【前台】用户查询资讯列表统计
   * @param [statFrontendInformationsQuery]
   * @param [statFrontendInformationsQuery.isReceived] 是否是接收列表
   * @param [statFrontendInformationsQuery.titleLike]
   * @param [statFrontendInformationsQuery.publishedAtEgt] 发送时间大于
   * @param [statFrontendInformationsQuery.publishedAtElt] 发送时间小于
   * @param [statFrontendInformationsQuery.method] 发送方式
   * @param [statFrontendInformationsQuery.status] 发送状态
   * @param [statFrontendInformationsQuery.createrNameLike] 发送人姓名
   * @param [statFrontendInformationsQuery.activityId]
   * @param [statFrontendInformationsQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<StatNotification>
   */
  statFrontendInformations<TQuery extends StatFrontendInformationsQuery>(
    statFrontendInformationsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<StatNotification, TQuery['expand']>> {
    return axios(
      notificationApi.statFrontendInformationsConfig(
        statFrontendInformationsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new StatNotification(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useStatFrontendInformations<TQuery extends StatFrontendInformationsQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<StatNotification, TQuery['expand']>> {
    return useQueryApi<Expand<StatNotification, TQuery['expand']>>(
      notificationApi.statFrontendInformations,
      'Notification.statFrontendInformations',
      queryOptions,
      query
    )
  },

  queryFrontendNoticesConfig(
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/notification/frontend-notices', {})
  },

  /**
   * @summary 【前台】查询用户提醒（支持下述平台消息提醒）
   *  - 网易企业邮箱：用户今日日程
   *  - 网易企业邮箱：未读邮件提醒
   *  - 希悦：用户今日日程
   *  - 希悦：未读通知
   *  - 希悦：未完成待办
   * @param [options]
   * @return AxiosResponsePromise<Notice>
   */
  queryFrontendNotices(
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Notice> {
    return axios(notificationApi.queryFrontendNoticesConfig(options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Notice(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useQueryFrontendNotices(
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Notice> {
    return useQueryApi<Notice>(
      notificationApi.queryFrontendNotices,
      'Notification.queryFrontendNotices',
      queryOptions
    )
  },

  readNoticeConfig(
    readNoticesReq: ReadNoticesReq[],
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/notification/frontend-notices/read',
      {},
      null,
      readNoticesReq,
      {
        format: 'json',
        isArray: true,
        defaults: {
          dataType: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 【前台】已读提醒
   * @param readNoticesReq
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  readNotice(
    readNoticesReq: ReadNoticesReq[],
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      notificationApi.readNoticeConfig(readNoticesReq, options)
    ) as any
  },

  useReadNotice(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: ReadNoticesReq[]) => notificationApi.readNotice(payload),
      mutationOptions
    )
  },

  createNotificationConfig(
    notificationReq: NotificationReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/notification/notifications',
      {},
      null,
      notificationReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          receivers: (v: any) => v || [],
          title: (v: any) => v || '',
          publishedAt: (v: any) => v || null,
          content: (v: any) => v || '',
          withdrawReason: (v: any) => v || null,
          attachment: (v: any) => v || null,
          setting: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 【后台】创建通知
   *  - 活动通知
   * @param notificationReq
   * @param [options]
   * @return AxiosResponsePromise<Notification>
   */
  createNotification(
    notificationReq: NotificationReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Notification> {
    return axios(
      notificationApi.createNotificationConfig(notificationReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Notification(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useCreateNotification(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: NotificationReq) => notificationApi.createNotification(payload),
      mutationOptions
    )
  },

  queryNotificationsConfig(
    queryNotificationsQuery?: QueryNotificationsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/notification/notifications',
      {},
      queryNotificationsQuery
    )
  },

  /**
   * @summary 【后台】查询通知列表
   *  - 活动通知
   *  - 资讯通知
   * @param [queryNotificationsQuery]
   * @param [queryNotificationsQuery.isActivity] 是否是活动通知，默认是
   * @param [queryNotificationsQuery.paginated] 是否分页，默认 1
   * @param [queryNotificationsQuery.page] 显示的数据页，默认 1
   * @param [queryNotificationsQuery.perPage] 每页显示数量，默认 20
   * @param [queryNotificationsQuery.sort]
   * @param [queryNotificationsQuery.titleLike]
   * @param [queryNotificationsQuery.publishedAtEgt] 发送时间大于
   * @param [queryNotificationsQuery.publishedAtElt] 发送时间小于
   * @param [queryNotificationsQuery.method] 发送方式
   * @param [queryNotificationsQuery.status] 发送状态
   * @param [queryNotificationsQuery.createrNameLike] 发送人姓名
   * @param [queryNotificationsQuery.activityId]
   * @param [queryNotificationsQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<Notification[]>
   */
  queryNotifications<TQuery extends QueryNotificationsQuery>(
    queryNotificationsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Notification, TQuery['expand']>[]> {
    return axios(
      notificationApi.queryNotificationsConfig(queryNotificationsQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new Notification(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryNotifications<TQuery extends QueryNotificationsQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Notification, TQuery['expand']>[]> {
    return useQueryApi<Expand<Notification, TQuery['expand']>[]>(
      notificationApi.queryNotifications,
      'Notification.queryNotifications',
      queryOptions,
      query
    )
  },

  deleteNotificationConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/notification/notifications/{id}', {
      id,
    })
  },

  /**
   * @summary 【后台】删除通知
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteNotification(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(notificationApi.deleteNotificationConfig(id, options)) as any
  },

  useDeleteNotification(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => notificationApi.deleteNotification(id),
      mutationOptions
    )
  },

  loadNotificationConfig(
    id: number,
    loadNotificationQuery?: LoadNotificationQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/notification/notifications/{id}',
      {
        id,
      },
      loadNotificationQuery
    )
  },

  /**
   * @summary 【后台】查询通知
   *  - 活动通知
   * @param id
   * @param [loadNotificationQuery]
   * @param [loadNotificationQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<Notification>
   */
  loadNotification<TQuery extends LoadNotificationQuery>(
    id: number,
    loadNotificationQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Notification, TQuery['expand']>> {
    return axios(
      notificationApi.loadNotificationConfig(id, loadNotificationQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Notification(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadNotification<TQuery extends LoadNotificationQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Notification, TQuery['expand']>> {
    return useQueryApi<Expand<Notification, TQuery['expand']>>(
      notificationApi.loadNotification,
      'Notification.loadNotification',
      queryOptions,
      id,
      query
    )
  },

  updateNotificationConfig(
    id: number,
    notificationReq: NotificationReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/notification/notifications/{id}',
      {
        id,
      },
      null,
      notificationReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          receivers: (v: any) => v || [],
          title: (v: any) => v || '',
          publishedAt: (v: any) => v || null,
          content: (v: any) => v || '',
          withdrawReason: (v: any) => v || null,
          attachment: (v: any) => v || null,
          setting: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 【后台】编辑通知
   *  - 活动通知
   * @param id
   * @param notificationReq
   * @param [options]
   * @return AxiosResponsePromise<Notification>
   */
  updateNotification(
    id: number,
    notificationReq: NotificationReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Notification> {
    return axios(
      notificationApi.updateNotificationConfig(id, notificationReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Notification(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateNotification(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: NotificationReq }) =>
        notificationApi.updateNotification(id, payload),
      mutationOptions
    )
  },

  readNotificationConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/notification/notifications/{id}/read',
      {
        id,
      }
    )
  },

  /**
   * @summary 【前台】已读通知
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  readNotification(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(notificationApi.readNotificationConfig(id, options)) as any
  },

  useReadNotification(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => notificationApi.readNotification(id),
      mutationOptions
    )
  },

  modifyNotificationStatusConfig(
    status: StatusEnum,
    id: number,
    withDrawnNotificationReq: WithDrawnNotificationReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/notification/notifications/{id}/status/{status}',
      {
        status,
        id,
      },
      null,
      withDrawnNotificationReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          withdrawReason: (v: any) => v || '',
        },
      }
    )
  },

  /**
   * @summary 【后台】编辑通知状态
   * @param status
   * @param id
   * @param withDrawnNotificationReq
   * @param [options]
   * @return AxiosResponsePromise<Notification>
   */
  modifyNotificationStatus(
    status: StatusEnum,
    id: number,
    withDrawnNotificationReq: WithDrawnNotificationReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Notification> {
    return axios(
      notificationApi.modifyNotificationStatusConfig(
        status,
        id,
        withDrawnNotificationReq,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Notification(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useModifyNotificationStatus(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        status,
        id,
        payload,
      }: {
        status: StatusEnum
        id: number
        payload: WithDrawnNotificationReq
      }) => notificationApi.modifyNotificationStatus(status, id, payload),
      mutationOptions
    )
  },

  queryReceivedNotificationsConfig(
    queryReceivedNotificationsQuery?: QueryReceivedNotificationsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/notification/received-notifications',
      {},
      queryReceivedNotificationsQuery
    )
  },

  /**
   * @summary 【前台】用户查询活动通知列表
   * @param [queryReceivedNotificationsQuery]
   * @param [queryReceivedNotificationsQuery.paginated] 是否分页，默认 1
   * @param [queryReceivedNotificationsQuery.page] 显示的数据页，默认 1
   * @param [queryReceivedNotificationsQuery.perPage] 每页显示数量，默认 20
   * @param [queryReceivedNotificationsQuery.sort]
   * @param [queryReceivedNotificationsQuery.titleLike]
   * @param [queryReceivedNotificationsQuery.publishedAtEgt] 发送时间大于
   * @param [queryReceivedNotificationsQuery.publishedAtElt] 发送时间小于
   * @param [queryReceivedNotificationsQuery.method] 发送方式
   * @param [queryReceivedNotificationsQuery.status] 发送状态
   * @param [queryReceivedNotificationsQuery.createrNameLike] 发送人姓名
   * @param [queryReceivedNotificationsQuery.activityId]
   * @param [queryReceivedNotificationsQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<Notification[]>
   */
  queryReceivedNotifications<TQuery extends QueryReceivedNotificationsQuery>(
    queryReceivedNotificationsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Notification, TQuery['expand']>[]> {
    return axios(
      notificationApi.queryReceivedNotificationsConfig(
        queryReceivedNotificationsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new Notification(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryReceivedNotifications<TQuery extends QueryReceivedNotificationsQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Notification, TQuery['expand']>[]> {
    return useQueryApi<Expand<Notification, TQuery['expand']>[]>(
      notificationApi.queryReceivedNotifications,
      'Notification.queryReceivedNotifications',
      queryOptions,
      query
    )
  },

  downloadNotificationTemplateConfig(
    reflectionDomain: UserForegroundDomainEnum,
    idIn: string,
    downloadNotificationTemplateQuery?: DownloadNotificationTemplateQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/notification/variable/download-template',
      {
        reflectionDomain,
        idIn,
      },
      downloadNotificationTemplateQuery
    )
  },

  /**
   * @summary 【后台】下载变量模板接口[根据选定的范围生成模板]
   * @param reflectionDomain 用户领域
   * @param idIn
   * @param [downloadNotificationTemplateQuery]
   * @param [downloadNotificationTemplateQuery.filename] 不带后缀文件名
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  downloadNotificationTemplate(
    reflectionDomain: UserForegroundDomainEnum,
    idIn: string,
    downloadNotificationTemplateQuery?: DownloadNotificationTemplateQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      notificationApi.downloadNotificationTemplateConfig(
        reflectionDomain,
        idIn,
        downloadNotificationTemplateQuery,
        options
      )
    ) as any
  },

  useDownloadNotificationTemplate(
    {
      reflectionDomain,
      idIn,
      query,
    }: {
      reflectionDomain: UserForegroundDomainEnum
      idIn: string
      query?: DownloadNotificationTemplateQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      notificationApi.downloadNotificationTemplate,
      'Notification.downloadNotificationTemplate',
      queryOptions,
      reflectionDomain,
      idIn,
      query
    )
  },

  parseVariableFileConfig(
    reflectionDomain: UserForegroundDomainEnum,
    uploadFile: UploadFile,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/notification/variable/upload-variables',
      {
        reflectionDomain,
      },
      null,
      uploadFile,
      {
        format: 'form',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【后台】上传变量文件接口[按用户类型+用户领域导入]
   * @param reflectionDomain 用户领域
   * @param uploadFile
   * @param [options]
   * @return AxiosResponsePromise<NotificationUploadResponse>
   */
  parseVariableFile(
    reflectionDomain: UserForegroundDomainEnum,
    uploadFile: UploadFile,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<NotificationUploadResponse> {
    return axios(
      notificationApi.parseVariableFileConfig(
        reflectionDomain,
        uploadFile,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new NotificationUploadResponse(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useParseVariableFile(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        reflectionDomain,
        payload,
      }: {
        reflectionDomain: UserForegroundDomainEnum
        payload: UploadFile
      }) => notificationApi.parseVariableFile(reflectionDomain, payload),
      mutationOptions
    )
  },

  queryNotificationReflectionsConfig(
    id: number,
    queryNotificationReflectionsQuery?: QueryNotificationReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/notification/{id}/reflections',
      {
        id,
      },
      queryNotificationReflectionsQuery
    )
  },

  /**
   * @summary 【后台】通知人员列表(完成情况)
   * @param id
   * @param [queryNotificationReflectionsQuery]
   * @param [queryNotificationReflectionsQuery.expand]
   * @param [queryNotificationReflectionsQuery.isReaded]
   * @param [queryNotificationReflectionsQuery.reflection.nameOrPhoneLikes] 根据名字或手机号筛选，多个用英文逗号分隔
   * @param [queryNotificationReflectionsQuery.reflection.nameIn]
   * @param [queryNotificationReflectionsQuery.reflection.nameLike]
   * @param [queryNotificationReflectionsQuery.reflection.accountLike]
   * @param [queryNotificationReflectionsQuery.reflection.accountIn]
   * @param [queryNotificationReflectionsQuery.reflection.genderIn]
   * @param [queryNotificationReflectionsQuery.reflection.statusIn]
   * @param [queryNotificationReflectionsQuery.reflection.phoneIn]
   * @param [queryNotificationReflectionsQuery.reflection.phoneLike]
   * @param [queryNotificationReflectionsQuery.reflection.emailIn]
   * @param [queryNotificationReflectionsQuery.reflection.emailLike]
   * @param [queryNotificationReflectionsQuery.sort]
   * @param [queryNotificationReflectionsQuery.paginated] 是否分页，默认 1
   * @param [queryNotificationReflectionsQuery.page] 显示的数据页，默认 1
   * @param [queryNotificationReflectionsQuery.perPage] 每页显示数量，默认 20
   * @param [options]
   * @return AxiosResponsePromise<NotificationReflection[]>
   */
  queryNotificationReflections<
    TQuery extends QueryNotificationReflectionsQuery
  >(
    id: number,
    queryNotificationReflectionsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<NotificationReflection, TQuery['expand']>[]> {
    return axios(
      notificationApi.queryNotificationReflectionsConfig(
        id,
        queryNotificationReflectionsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data
        ? _data.map((d: any) => new NotificationReflection(d))
        : _data
      return { data, ...rest }
    }) as any
  },

  useQueryNotificationReflections<
    TQuery extends QueryNotificationReflectionsQuery
  >(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<NotificationReflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<NotificationReflection, TQuery['expand']>[]>(
      notificationApi.queryNotificationReflections,
      'Notification.queryNotificationReflections',
      queryOptions,
      id,
      query
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(notificationApi).forEach((api) => {
  // @ts-ignore
  notificationApi[api]._name_ = `Notification.${api}`
})
